import gql from 'graphql-tag';

import {getChemicalFileContent, IS_CHEMICAL_FILE_FORMAT_BINARY} from 'chemistry/ChemicalFileUtil';
import {
  ChemicalStructureBlob,
  CHEMICAL_FILE_FORMAT_TO_EXTENSION,
  ChemicalFileFormat,
} from 'chemistry/ChemistryConstants';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import dataURIToBlob from 'notebook/notes/attachments/images/dataURIToBlob';
import {ExternalFile} from 'notebook/util/NoteUtilConstants';
import {toFilename} from 'util/FileUtil';
import {textToBlob, initiateFileDownload, initiateTextDownload} from 'util/TextToBlobUtil';

import {
  DownloadChemicalFileQuery,
  DownloadChemicalFileQueryVariables,
} from './__generated/DownloadChemicalFileQuery';

export const DOWNLOAD_CHEMICAL_FILE_QUERY = gql`
  query DownloadChemicalFileQuery(
    $apiId: StrictString!
    $url: StrictString!
    $owningEntityApiId: StrictString
    $versionId: StrictInt
  ) @skipAudit @engComponent(name: CHEMISTRY) {
    signedFile(
      apiId: $apiId
      url: $url
      addHtmlContentTypeHeader: false
      owningEntityApiId: $owningEntityApiId
      versionId: $versionId
    ) {
      ... on SignedFileNode {
        signedUrl
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const stubbableFunctions = {
  async chemicalFileSignedURL(externalFile: ExternalFile): Promise<string> {
    const externalFileURL = await doQueryWithoutCache<
      DownloadChemicalFileQuery,
      DownloadChemicalFileQueryVariables
    >(
      {query: DOWNLOAD_CHEMICAL_FILE_QUERY},
      {variables: {apiId: externalFile.id, url: externalFile.url, owningEntityApiId: '', versionId: null}}
    );
    if (isInaccessible(externalFileURL.signedFile)) {
      return '';
    }
    const {signedUrl} = externalFileURL.signedFile;
    return signedUrl;
  },
};

export async function chemicalStructureFromFile(
  externalFile: ExternalFile,
  fileType: ChemicalFileFormat
): Promise<string> {
  const externalFileURL = await stubbableFunctions.chemicalFileSignedURL(externalFile);
  try {
    const response = await fetch(externalFileURL);
    return await getChemicalFileContent(fileType, response);
  } catch (err) {
    throw new Error('Unable to open structure for editing.');
  }
}

export function createChemicalStructureBlob(
  structure: string,
  basename: string,
  saveFormat: ChemicalFileFormat
): ChemicalStructureBlob {
  const extension = CHEMICAL_FILE_FORMAT_TO_EXTENSION[saveFormat];
  const filename = toFilename(basename, extension);

  let chemicalBlob: ChemicalStructureBlob;
  if (IS_CHEMICAL_FILE_FORMAT_BINARY[saveFormat]) {
    const dataURI = `data:binary/octet-stream;base64,${structure}`;
    chemicalBlob = dataURIToBlob(dataURI, filename);
  } else {
    chemicalBlob = textToBlob(structure);
    chemicalBlob.name = filename;
  }
  return chemicalBlob;
}

/**
 * Based on initiateTextDownload in client/util/TextToBlobUtil.ts,
 * modified to support binary chemical file formats
 */
export function downloadChemicalStructure(
  structure: string,
  filename: string,
  format: ChemicalFileFormat
): void {
  if (IS_CHEMICAL_FILE_FORMAT_BINARY[format]) {
    const dataURI = `data:binary/octet-stream;base64,${structure}`;
    initiateFileDownload(filename, dataURI);
  } else {
    initiateTextDownload(structure, filename);
  }
}
